import React from "react";
import { Box, IconButton, Stack, Tabs, Tab, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import {
  CustomButton,
  CustomModal,
  CustomTextInput,
  CustomSingleSelect,
  CustomFileUploader,
} from "@ippon/shared-components";
import { useForm } from "react-hook-form";

interface Boundary {
  adjacentLand: string;
  confirmation: any[];
  encroachment: string;
  encroachmentMemo: any[];
}

interface Lot {
  lotNumber: string;
  boundaries: Boundary[];
}

interface LotBoundaryModalProps {
  open: boolean;
  onClose: () => void;
  editingIndex: number | null;
  modalLot: Lot;
  onSave: (lot: Lot) => void;
  addBoundaryInModal: () => void;
  removeBoundaryInModal: (index: number) => void;
}

const LotBoundaryModal: React.FC<LotBoundaryModalProps> = ({
  open,
  onClose,
  editingIndex,
  modalLot,
  onSave,
  addBoundaryInModal,
  removeBoundaryInModal,
}) => {
  const {
    register,
    control,
    formState: { errors, isDirty },
    setValue,
    getValues,
    watch,
  } = useForm();

  // フォームの値を監視
  const watchedValues = watch();

  // アクティブなタブのインデックス（境界のインデックス、-1は+タブ）
  const [activeTabIndex, setActiveTabIndex] = React.useState<number>(0);
  const tabsRef = React.useRef<HTMLDivElement>(null);
  const [selectedTabRect, setSelectedTabRect] = React.useState<{ left: number; width: number } | null>(null);
  // +タブをクリックしたかどうかのフラグ
  const [shouldSwitchToNewTab, setShouldSwitchToNewTab] =
    React.useState<boolean>(false);
  const prevBoundaryCountRef = React.useRef<number>(modalLot.boundaries.length);
  const prevOpenRef = React.useRef<boolean>(false);
  // 編集開始時の境界数を保持（境界の追加・削除を変更として検知するため）
  const originalBoundaryCountRef = React.useRef<number>(modalLot.boundaries.length);
  const [originalBoundaryCount, setOriginalBoundaryCount] = React.useState<number>(modalLot.boundaries.length);

  // ボタンの有効/無効を判定
  const isButtonDisabled = React.useMemo(() => {
    if (editingIndex === null) {
      // 新規追加時：地番・境界データ・境界追加のいずれかがあればアクティブ
      const hasLotNumber = watchedValues.lotNumber?.trim();
      const hasBoundaryAdded =
        modalLot.boundaries.length > originalBoundaryCount;
      const hasAnyBoundaryData = modalLot.boundaries.some((_, index) => {
        const adjacentLand = watchedValues[`adjacentLand_${index}`]?.trim();
        const encroachment = watchedValues[`encroachment_${index}`];
        const confirmation = watchedValues[`confirmation_${index}`];
        const encroachmentMemo = watchedValues[`encroachmentMemo_${index}`];

        return (
          adjacentLand ||
          encroachment ||
          (confirmation && confirmation.length > 0) ||
          (encroachmentMemo && encroachmentMemo.length > 0)
        );
      });

      return !hasLotNumber && !hasAnyBoundaryData && !hasBoundaryAdded;
    } else {
      // 編集時：変更がない場合は非アクティブ（境界数の増減も変更として扱う）
      const hasChanges =
        watchedValues.lotNumber !== modalLot.lotNumber ||
        modalLot.boundaries.length !== originalBoundaryCount ||
        modalLot.boundaries.some((boundary, index) => {
          const adjacentLand = watchedValues[`adjacentLand_${index}`];
          const encroachment = watchedValues[`encroachment_${index}`];
          const confirmation = watchedValues[`confirmation_${index}`];
          const encroachmentMemo = watchedValues[`encroachmentMemo_${index}`];

          return (
            adjacentLand !== boundary.adjacentLand ||
            encroachment !== boundary.encroachment ||
            JSON.stringify(confirmation) !==
              JSON.stringify(boundary.confirmation) ||
            JSON.stringify(encroachmentMemo) !==
              JSON.stringify(boundary.encroachmentMemo)
          );
        });

      return !hasChanges;
    }
  }, [watchedValues, editingIndex, modalLot, originalBoundaryCount]);

  const encroachmentOptions = [
    { label: "あり", value: "あり" },
    { label: "なし", value: "なし" },
  ];

  // modalLotの変更をフォームに同期（モーダルを開いたときのみ）
  React.useEffect(() => {
    if (open && !prevOpenRef.current) {
      setValue("lotNumber", modalLot.lotNumber);
      modalLot.boundaries.forEach((boundary, index) => {
        setValue(`adjacentLand_${index}`, boundary.adjacentLand || "");
        setValue(`encroachment_${index}`, boundary.encroachment || "");
        setValue(`confirmation_${index}`, boundary.confirmation || []);
        setValue(`encroachmentMemo_${index}`, boundary.encroachmentMemo || []);
      });

      prevBoundaryCountRef.current = modalLot.boundaries.length;
      // 編集開始時の境界数を記録
      originalBoundaryCountRef.current = modalLot.boundaries.length;
      setOriginalBoundaryCount(modalLot.boundaries.length);

      // モーダルを開いたときは最初のタブを選択
      setActiveTabIndex(0);
      setShouldSwitchToNewTab(false);
    }
    prevOpenRef.current = open;
  }, [open, modalLot, setValue]);

  // 境界数が増えたときのみ新しいフィールドを初期化
  React.useEffect(() => {
    if (!open) {
      prevBoundaryCountRef.current = modalLot.boundaries.length;
      return;
    }

    const prevCount = prevBoundaryCountRef.current;
    const currentCount = modalLot.boundaries.length;

    if (currentCount > prevCount) {
      for (let i = prevCount; i < currentCount; i++) {
        const boundary = modalLot.boundaries[i];
        setValue(`adjacentLand_${i}`, boundary?.adjacentLand || "");
        setValue(`encroachment_${i}`, boundary?.encroachment || "");
        setValue(`confirmation_${i}`, boundary?.confirmation || []);
        setValue(`encroachmentMemo_${i}`, boundary?.encroachmentMemo || []);
      }
    }

    prevBoundaryCountRef.current = currentCount;
  }, [modalLot.boundaries, open, setValue]);

  // 境界が追加されたときのタブ切り替え処理
  React.useEffect(() => {
    if (shouldSwitchToNewTab && modalLot.boundaries.length > 0) {
      // 新しく追加された境界のタブに切り替え
      setActiveTabIndex(modalLot.boundaries.length - 1);
      setShouldSwitchToNewTab(false);
      // 追加後に scroller を右端までスクロール
      requestAnimationFrame(() => {
        if (!tabsRef.current) return;
        const scroller = tabsRef.current.querySelector('.MuiTabs-scroller') as HTMLElement | null;
        if (scroller) scroller.scrollLeft = scroller.scrollWidth;
      });
    }
  }, [modalLot.boundaries.length, shouldSwitchToNewTab]);

  // 境界が削除されたときのタブ切り替え処理
  React.useEffect(() => {
    // アクティブタブが存在しない場合（削除された場合など）
    if (activeTabIndex >= modalLot.boundaries.length && !shouldSwitchToNewTab) {
      // 最後のタブに切り替え（存在する場合）
      if (modalLot.boundaries.length > 0) {
        setActiveTabIndex(modalLot.boundaries.length - 1);
      } else {
        setActiveTabIndex(0);
      }
    }
  }, [modalLot.boundaries.length, activeTabIndex, shouldSwitchToNewTab]);

  // 選択タブの位置を計測
  const measureSelectedTab = React.useCallback(() => {
    if (!tabsRef.current) return;
    const tabEls = tabsRef.current.querySelectorAll('[role="tab"]');
    const selectedTab = tabEls[activeTabIndex] as HTMLElement | undefined;
    if (selectedTab) {
      const tabsRect = tabsRef.current.getBoundingClientRect();
      const tabRect = selectedTab.getBoundingClientRect();
      setSelectedTabRect({
        left: tabRect.left - tabsRect.left,
        width: tabRect.width,
      });
    }
  }, [activeTabIndex]);

  React.useEffect(() => {
    const id = requestAnimationFrame(measureSelectedTab);
    return () => cancelAnimationFrame(id);
  }, [measureSelectedTab, modalLot.boundaries.length, open]);

  // scrollイベントでも再計測
  React.useEffect(() => {
    if (!tabsRef.current) return;
    const scroller = tabsRef.current.querySelector('.MuiTabs-scroller') as HTMLElement | null;
    if (!scroller) return;
    scroller.addEventListener('scroll', measureSelectedTab);
    return () => scroller.removeEventListener('scroll', measureSelectedTab);
  }, [measureSelectedTab]);

  // タブ切り替えハンドラ
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    // newValueが境界の数と同じ場合は+タブ
    if (newValue === modalLot.boundaries.length) {
      // 新しい境界を追加
      addBoundaryInModal();
      // 追加された境界のタブに切り替えるフラグを立てる
      setShouldSwitchToNewTab(true);
    } else {
      setActiveTabIndex(newValue);
    }
  };

  // 境界削除ハンドラ（タブ内の削除ボタン用）
  const handleRemoveBoundary = (
    boundaryIndex: number,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();

    // 現在のフォーム値を保持
    const boundaryValues = modalLot.boundaries.map((_, index) => ({
      adjacentLand: getValues(`adjacentLand_${index}`) || "",
      encroachment: getValues(`encroachment_${index}`) || "",
      confirmation: getValues(`confirmation_${index}`) || [],
      encroachmentMemo: getValues(`encroachmentMemo_${index}`) || [],
    }));

    boundaryValues.splice(boundaryIndex, 1);

    // 削除前に新しいアクティブタブのインデックスを計算
    let newActiveIndex = activeTabIndex;
    if (boundaryIndex === activeTabIndex) {
      // 削除されるタブがアクティブな場合
      if (modalLot.boundaries.length > 1) {
        // 前のタブに切り替え（存在する場合）
        newActiveIndex = boundaryIndex > 0 ? boundaryIndex - 1 : 0;
      } else {
        newActiveIndex = 0;
      }
    } else if (boundaryIndex < activeTabIndex) {
      // 削除されるタブがアクティブタブより前の場合、インデックスを1つ減らす
      newActiveIndex = activeTabIndex - 1;
    }

    // 境界を削除
    removeBoundaryInModal(boundaryIndex);

    // 即座にアクティブタブを更新
    setActiveTabIndex(newActiveIndex);

    // フォーム値を再設定してインデックスを詰める
    boundaryValues.forEach((value, index) => {
      setValue(`adjacentLand_${index}`, value.adjacentLand || "");
      setValue(`encroachment_${index}`, value.encroachment || "");
      setValue(`confirmation_${index}`, value.confirmation || []);
      setValue(`encroachmentMemo_${index}`, value.encroachmentMemo || []);
    });

    // 余分なフィールドをクリア
    for (let i = boundaryValues.length; i < prevBoundaryCountRef.current; i++) {
      setValue(`adjacentLand_${i}`, "");
      setValue(`encroachment_${i}`, "");
      setValue(`confirmation_${i}`, []);
      setValue(`encroachmentMemo_${i}`, []);
    }
    prevBoundaryCountRef.current = boundaryValues.length;
  };

  // 保存時にフォームの値をmodalLotに反映
  const handleSave = () => {
    const formValues = getValues();
    const updatedLot: Lot = {
      lotNumber: formValues.lotNumber || "",
      boundaries: modalLot.boundaries.map((_, index) => ({
        adjacentLand: formValues[`adjacentLand_${index}`] || "",
        confirmation: formValues[`confirmation_${index}`] || [],
        encroachment: formValues[`encroachment_${index}`] || "",
        encroachmentMemo: formValues[`encroachmentMemo_${index}`] || [],
      })),
    };
    onSave(updatedLot);
  };

  return (
    <CustomModal
      title={editingIndex !== null ? "地番・境界を編集" : "地番・境界を追加"}
      openModal={open}
      handleCloseModal={onClose}
      modalType="wideModal"
      addTitleBorder
      confirmOnDirtyClose={isDirty}
    >
      {/* スクロール可能なコンテンツラッパー */}
      <Box
        sx={{
          maxHeight: { xs: "70vh", sm: "80vh" },
          overflowY: "auto",
          flex: 1,
          px: 1.2,
        }}
      >
        {/* 地番入力エリア */}
        <Box sx={{ my: 2 }}>
          <CustomTextInput
            label="地番"
            name="lotNumber"
            placeholder="地番を入力してください"
            register={register}
            errors={errors}
            labelWidth="120px"
            inputWidth="100%"
            inputWidthSp="100%"
            inputMode="numeric"
          />
        </Box>

        {/* タブUIとコンテンツを一体化 */}
        <Box sx={{ mt: 2, position: "relative" }}>
          <Tabs
            ref={tabsRef}
            value={activeTabIndex}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons={false}
            sx={{
              backgroundColor: "white",
              minHeight: "48px",
              "& .MuiTabs-indicator": {
                display: "none",
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontFamily: '"Noto Sans JP", sans-serif',
                fontSize: "14px",
                fontWeight: 700,
                color: "#989898",
                minHeight: "48px",
                padding: "12px 24px",
                borderRadius: "8px 8px 0 0",
                backgroundColor: "transparent",
                border: "1px solid transparent",
                borderBottom: "none",
                "&.Mui-selected": {
                  color: "#0B9DBD",
                  backgroundColor: "#fff",
                  border: "none",
                  boxShadow: "inset 0 1px 0 #D9D9D9, inset 1px 0 0 #D9D9D9, inset -1px 0 0 #D9D9D9",
                  zIndex: 2,
                  position: "relative",
                },
              },
            }}
          >
            {/* 境界タブ */}
            {modalLot.boundaries.map((_, boundaryIndex) => (
              <Tab
                key={`boundary-tab-${boundaryIndex}`}
                data-no-confirm="true"
                label={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      gap: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 700,
                      }}
                    >
                      境界
                      <span style={{ fontSize: "13px", marginLeft: 2 }}>
                        {boundaryIndex + 1}
                      </span>
                    </Typography>
                    {/* 削除ボタン（最初の境界以外） */}
                    {boundaryIndex > 0 && (
                      <IconButton
                        size="small"
                        onClick={(e) => handleRemoveBoundary(boundaryIndex, e)}
                        sx={{
                          padding: "2px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        <CloseIcon
                          sx={{ fontSize: "16px", display: "block" }}
                        />
                      </IconButton>
                    )}
                  </Box>
                }
                value={boundaryIndex}
              />
            ))}
            {/* +タブ */}
            <Tab
              icon={<AddIcon sx={{ fontSize: "20px" }} />}
              label=""
              value={modalLot.boundaries.length}
              data-no-confirm="true"
              sx={{
                minWidth: "48px",
                padding: "12px 8px",
              }}
            />
          </Tabs>

          {/* 選択タブ下の境界線を白で消すオーバーレイ */}
          {selectedTabRect && (
            <Box
              sx={{
                position: "absolute",
                top: "47px",
                left: `${selectedTabRect.left}px`,
                width: `${selectedTabRect.width - 2}px`,
                height: "3px",
                backgroundColor: "#fff",
                zIndex: 3,
                pointerEvents: "none",
              }}
            />
          )}

          {/* 境界コンテンツ（各境界ごとに保持し、表示中のタブのみ表示） */}
          {modalLot.boundaries.map((boundary, boundaryIndex) => (
            <Box
              key={`boundary-content-${boundaryIndex}`}
              sx={{
                backgroundColor: "#fff",
                p: 2,
                border: "1px solid #D9D9D9",
                borderRadius: activeTabIndex === 0 ? "0 8px 8px 8px" : "8px",
                display: boundaryIndex === activeTabIndex ? "block" : "none",
              }}
            >
              {/* 隣地 */}
              <CustomTextInput
                label="隣地"
                name={`adjacentLand_${boundaryIndex}`}
                placeholder="隣地を入力してください"
                register={register}
                errors={errors}
                labelWidth="120px"
                inputWidth="100%"
                inputWidthSp="100%"
              />

              {/* 越境 */}
              <CustomSingleSelect
                label="越境"
                name={`encroachment_${boundaryIndex}`}
                placeholder="選択してください"
                control={control}
                errors={errors}
                options={encroachmentOptions}
                labelWidth="120px"
                inputWidth="100%"
              />

              {/* 確定の書類 */}
              <Box sx={{ mb: 2 }}>
                <CustomFileUploader
                  label="確定書類"
                  name={`confirmation_${boundaryIndex}`}
                  setValue={(n: any, v: any) => setValue(n, v)}
                  register={register as any}
                  width="180px"
                  initialImages={(() => {
                    const originalData = boundary?.confirmation || [];

                    const mapped = originalData.map((item: any) => {
                      // URL文字列の場合はそのまま
                      if (typeof item === "string") {
                        return item;
                      }
                      // CustomFileUploaderが返す形式: {isUrl: true, data: "url", id: "..."}
                      if (
                        item &&
                        item.isUrl &&
                        item.data &&
                        typeof item.data === "string"
                      ) {
                        return item.data;
                      }
                      // urlプロパティを持つオブジェクトの場合
                      if (item && item.url && typeof item.url === "string") {
                        return item.url;
                      }
                      // ImageDataオブジェクト（data, originalFileName）の場合
                      if (item && item.data && item.originalFileName) {
                        return item;
                      }
                      return item;
                    });
                    return mapped;
                  })()}
                />
              </Box>

              {/* 越境覚書 */}
              <Box>
                <CustomFileUploader
                  label="越境覚書"
                  name={`encroachmentMemo_${boundaryIndex}`}
                  setValue={(n: any, v: any) => setValue(n, v)}
                  register={register as any}
                  width="180px"
                  initialImages={(() => {
                    const originalData = boundary?.encroachmentMemo || [];

                    const mapped = originalData.map((item: any) => {
                      // URL文字列の場合はそのまま
                      if (typeof item === "string") {
                        return item;
                      }
                      // CustomFileUploaderが返す形式: {isUrl: true, data: "url", id: "..."}
                      if (
                        item &&
                        item.isUrl &&
                        item.data &&
                        typeof item.data === "string"
                      ) {
                        return item.data;
                      }
                      // urlプロパティを持つオブジェクトの場合
                      if (item && item.url && typeof item.url === "string") {
                        return item.url;
                      }
                      // ImageDataオブジェクト（data, originalFileName）の場合
                      if (item && item.data && item.originalFileName) {
                        return item;
                      }
                      // それ以外はそのまま
                      return item;
                    });

                    return mapped;
                  })()}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* 登録/更新ボタン（固定） */}
      <Stack direction="row" justifyContent="center" mt={2.5}>
        <CustomButton
          label={editingIndex !== null ? "更新" : "新規登録"}
          buttonCategory="submit"
          onClick={handleSave}
          disabled={isButtonDisabled}
        />
      </Stack>
    </CustomModal>
  );
};

export default LotBoundaryModal;
